/**
 * Support utilities for keeping FeatureFlagConfig.ts extremely human-readable.
 * This file absorbs the type, defaulting, and normalization work needed to turn
 * the compact config entries into the richer shape used by the API layer and app.
 */

export interface BehaviorMappingActions {
  mapper: { [key: string]: string };
  show: string[];
  showAll: boolean;
  useMapper: boolean;
  hide?: string[];
}

type NormalizeFlagValue<T> = (value: unknown, fallback: T) => T;

export type FamaFlagDefinition<T> = {
  ldKey: string;
  default: T;
  normalize: NormalizeFlagValue<T>;
};

type BooleanFamaFlagDefinition = Omit<
  FamaFlagDefinition<boolean>,
  "normalize"
> & {
  normalize?: NormalizeFlagValue<boolean>;
};

type FamaFlagDefinitionInput<T> = T extends boolean
  ? BooleanFamaFlagDefinition
  : FamaFlagDefinition<T>;

type FamaFlagDefinitionInputs = Record<string, FamaFlagDefinitionInput<any>>;

type NormalizedFamaFlagDefinitions<T extends FamaFlagDefinitionInputs> = {
  [K in keyof T]: T[K] extends { default: infer V }
    ? FamaFlagDefinition<V>
    : never;
};

type WidenFlagValue<T> = T extends boolean
  ? boolean
  : T extends string
  ? string
  : T extends number
  ? number
  : T;

export type FamaFlagValue<T> = T extends FamaFlagDefinition<infer V>
  ? WidenFlagValue<V>
  : never;

export function defineFamaFlagDefinitions<T extends FamaFlagDefinitionInputs>(
  definitions: T
): NormalizedFamaFlagDefinitions<T> {
  const normalized: Record<string, FamaFlagDefinition<any>> = {};
  for (const [key, definition] of Object.entries(definitions)) {
    normalized[key] = {
      ...definition,
      normalize: definition.normalize ?? normalizeBooleanFlag,
    };
  }
  return normalized as NormalizedFamaFlagDefinitions<T>;
}

export type BulkFeatureFlagValues = Record<string, unknown>;

export function booleanFeatureFlags<T extends Record<string, string>>(
  defaultValue: boolean,
  flags: T
): { [K in keyof T]: BooleanFamaFlagDefinition } {
  const definitions = {} as { [K in keyof T]: BooleanFamaFlagDefinition };
  for (const [appKey, ldKey] of Object.entries(flags)) {
    definitions[appKey as keyof T] = { ldKey, default: defaultValue };
  }
  return definitions;
}

function normalizeBooleanFlag(value: unknown, fallback: boolean): boolean {
  if (value == null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
  }
  if (typeof value === "number") return Boolean(value);
  return fallback;
}

export function normalizeBehaviorMapping(
  raw: unknown,
  fallback: BehaviorMappingActions
): BehaviorMappingActions {
  if (!raw || typeof raw !== "object") {
    return fallback;
  }
  const o = raw as Record<string, unknown>;
  return {
    hide: Array.isArray(o.hide) ? (o.hide as string[]) : fallback.hide ?? [],
    mapper:
      typeof o.mapper === "object" &&
      o.mapper !== null &&
      !Array.isArray(o.mapper)
        ? (o.mapper as { [key: string]: string })
        : fallback.mapper,
    show: Array.isArray(o.show) ? (o.show as string[]) : fallback.show,
    showAll: Boolean(o.showAll ?? fallback.showAll),
    useMapper: Boolean(o.useMapper ?? fallback.useMapper),
  };
}
